<script setup lang="ts">
import ContestsPreview from '@/views/contests/Components/ContestsPreview.vue'
import LitepieDatepicker from 'litepie-datepicker-tw3'

import Socials from '@/components/Socials.vue'

import useSessionStorage from '@/composables/sessionStorage'
import StoreUrlsModal from '@/components/StoreUrlsModal.vue'

import type { Boost, CategoryIds, Contest, Store, StoreUrl } from './types/global'

const { objectiveSlug } = useStore()
const route = useRoute()
const isUserFirstContest = computed(() => !!route?.query?.isNew)

const props = defineProps<{
  getContest: Contest
  isEditMode: boolean
  isTemplateMode: boolean
  categoriesIds: CategoryIds
}>()

const { t } = useI18n()
const { getStoreById, getStoresByPermission } = useStore()
const webappPreview = computed(() => `${import.meta.env.VITE_AWS_STORAGE_URL}/contest-games/webAppPreview.png`)

const boosts: Boost[] = [
  {
    id: 'none',
    icon: 'HeroUserGroupIconSolid',
    title: t('pages.contests.contestsCreatePage.global.form.fields.boost.none.title'),
    description: t('pages.contests.contestsCreatePage.global.form.fields.boost.none.description')
  },
  {
    id: 'facebook',
    icon: 'IconoirFacebook',
    title: t('pages.contests.contestsCreatePage.global.form.fields.boost.facebook.title'),
    description: t('pages.contests.contestsCreatePage.global.form.fields.boost.facebook.description')
  },
  {
    id: 'instagram',
    icon: 'IconoirInstagram',
    title: t('pages.contests.contestsCreatePage.global.form.fields.boost.instagram.title'),
    description: t('pages.contests.contestsCreatePage.global.form.fields.boost.instagram.description')
  },
  {
    id: 'google',
    icon: 'IconoirGoogleCircle',
    title: t('pages.contests.contestsCreatePage.global.form.fields.boost.google.title'),
    description: t('pages.contests.contestsCreatePage.global.form.fields.boost.google.description')
  },
  {
    id: 'custom',
    icon: 'HeroArrowTopRightOnSquareIconSolid',
    title: t('pages.contests.contestsCreatePage.global.form.fields.boost.custom.title'),
    description: t('pages.contests.contestsCreatePage.global.form.fields.boost.custom.description')
  }
]

const now = new Date()

const { merchantName } = useLocalStorage()

const { isEmptyDuplicateContestItem } = useSessionStorage()

const nowFormatted = `${now.getFullYear()}-${`0${now.getMonth() + 1}`.slice(-2)}-${`0${now.getDate()}`.slice(-2)}`

const contest = ref(props.getContest)
const dateRangeValue = ref([])
const selectedBoost = ref(boosts[0])
const editMode = ref(props.isEditMode)

const playableOnceADayItems = [
  {
    id: 'once',
    title: t('pages.contests.contestsCreatePage.global.form.fields.isPlayableOnceADay.once.title'),
    description: t('pages.contests.contestsCreatePage.global.form.fields.isPlayableOnceADay.once.description')
  },
  {
    id: 'everyday',
    title: t('pages.contests.contestsCreatePage.global.form.fields.isPlayableOnceADay.everyday.title'),
    description: t('pages.contests.contestsCreatePage.global.form.fields.isPlayableOnceADay.everyday.description')
  },
  {
    id: 'customize',
    title: t('pages.contests.contestsCreatePage.global.form.fields.isPlayableOnceADay.customize.title'),
    description: t('pages.contests.contestsCreatePage.global.form.fields.isPlayableOnceADay.customize.description')
  }
]

const selectedPlayableFrequency = ref(playableOnceADayItems[0])

const disableDate = (date) => {
  const dateFormatted = `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(-2)}-${`0${date.getDate()}`.slice(-2)}`
  return dateFormatted < nowFormatted
}

const dateRangeChanged = (newDateRangeValue: string[]) => {
  contest.value.fromDate = newDateRangeValue[0] ?? null
  contest.value.toDate = newDateRangeValue[1] ?? null
}

const checkAll = () => {
  uncheckAll()
  storesPermission.value.forEach((store) => {
    if (isStoreAvailable(store.id)) {
      contest.value.stores.push(store.id)
    }
  })
}
const uncheckAll = () => {
  contest.value.stores = []
}

const hasGoogle = (store: Store) => {
  return store.urls?.map((url: StoreUrl) => url.storeUrlCategoryId === props.categoriesIds.googleId)?.includes(true)
}
const hasFacebook = (store: Store) => {
  return store.urls?.map((url: StoreUrl) => url.storeUrlCategoryId === props.categoriesIds.facebookId)?.includes(true)
}
const hasInstagram = (store: Store) => {
  return store.urls?.map((url: StoreUrl) => url.storeUrlCategoryId === props.categoriesIds.instagramId)?.includes(true)
}

const isStoreAvailable = (storeId: number) => {
  if (['none', 'custom'].includes(selectedBoost.value?.id)) {
    return true
  }

  const store = getStoreById(storeId)

  if (selectedBoost.value?.id === 'facebook' && !hasFacebook(store)) {
    return false
  }
  if (selectedBoost.value?.id === 'instagram' && !hasInstagram(store)) {
    return false
  }
  if (selectedBoost.value?.id === 'google' && !hasGoogle(store)) {
    return false
  }
  return true
}

const getStoresByItemId = (item: string) => {
  uncheckAll()
  storesPermission.value.forEach((store) => {
    if (item === 'facebook' && hasFacebook(store)) {
      contest.value.stores.push(store.id)
    }
    if (item === 'instagram' && hasInstagram(store)) {
      contest.value.stores.push(store.id)
    }
    if (item === 'google' && hasGoogle(store)) {
      contest.value.stores.push(store.id)
    }
    if (item === 'custom') {
      contest.value.stores.push(store.id)
    }
    updateBoostDefaultTexts()
  })
}
const emitSelectedBoost = (item: Boost) => {
  emit('selected-boost', selectedBoost.value)

  updateBoostDefaultTexts()

  getStoresByItemId(item.id)

  if (item.id === 'custom' || item.id === 'none') {
    checkAll()
  }

  if (contest.value.contestGameBoostType !== undefined) {
    contest.value.contestGameBoostType.slug = item.id
  }
}

const getBoostDefaultAskActionTitle = (boostId: string) => {
  const key = `pages.contests.contestsCreatePage.preview.askActionTitle.${boostId}`
  const translated = t(key)

  return translated === key ? t('pages.contests.contestsCreatePage.preview.askActionTitle.custom') : translated
}

const updateBoostDefaultTexts = () => {
  const boostId = selectedBoost.value?.id
  if (!boostId) {
    return
  }

  contest.value.askActionTitle = getBoostDefaultAskActionTitle(boostId)

  if (boostId === 'facebook') {
    contest.value.askActionButtonText = t('pages.contests.contestsCreatePage.preview.askActionButtonTextLike')
  }
  if (boostId === 'instagram') {
    contest.value.askActionButtonText = t('pages.contests.contestsCreatePage.preview.askActionButtonTextFollow')
  }
  if (boostId === 'google') {
    contest.value.askActionButtonText = t('pages.contests.contestsCreatePage.preview.askActionButtonText')
  }
  if (boostId === 'custom') {
    contest.value.askActionButtonText = t('pages.contests.contestsCreatePage.preview.askActionButtonTextCustom')
  }
}

watch(
  () => selectedBoost.value?.id,
  () => {
    updateBoostDefaultTexts()
  }
)

const emit = defineEmits<{
  (e: 'selected-boost', selectedBoost: Boost): void
  (
    e: 'selected-frequency',
    selectedFrequency: {
      id: string
      title: string
      description: string
    }
  ): void
}>()

emit('selected-frequency', selectedPlayableFrequency.value)

watch(selectedPlayableFrequency, () => {
  emit('selected-frequency', selectedPlayableFrequency.value)
  switch (selectedPlayableFrequency.value.id) {
    case 'everyday':
      contest.value.daysBetweenParticipations = 1
      contest.value.isPlayableManyTimes = true
      break
    case 'once':
      contest.value.isPlayableManyTimes = false
      contest.value.daysBetweenParticipations = 0
      break
    case 'customize':
      contest.value.daysBetweenParticipations =
        contest.value.daysBetweenParticipations < 2 ? 7 : contest.value.daysBetweenParticipations
      contest.value.isPlayableManyTimes = true
      break
  }
})

if (editMode.value || !isEmptyDuplicateContestItem.value) {
  let index
  switch (contest.value.daysBetweenParticipations) {
    case 0:
      index = 0
      break
    case 1:
      index = 1
      break
    default:
      index = 2
  }

  selectedPlayableFrequency.value = playableOnceADayItems[index]

  selectedBoost.value = boosts.find((boost) => boost.id === contest.value.contestGameBoostType?.slug)

  dateRangeValue.value = [contest.value.fromDate, contest.value.toDate]
} else {
  if (!props.isTemplateMode) {
    contest.value.name = t('pages.contests.contestsCreatePage.global.form.fields.defaultName', {
      name: merchantName.value
    })
  }
}

const channelType = [
  {
    id: 1,
    slug: 'sms',
    title: t('pages.contests.contestsCreatePage.message.form.fields.communicationChannel.sms.title'),
    description: t('pages.contests.contestsCreatePage.message.form.fields.communicationChannel.sms.description')
  },
  {
    id: 2,
    slug: 'mail',
    title: t('pages.contests.contestsCreatePage.message.form.fields.communicationChannel.email.title'),
    description: t('pages.contests.contestsCreatePage.message.form.fields.communicationChannel.email.description')
  }
]
const channelTypeModel = ref(
  channelType.filter((channel) => channel.slug === contest.value.messageTemplate.messageChannel?.slug)[0] ||
    channelType[0]
)

const placeholderReward = ref('{{ reward }}')
const placeholderQrcodeLink = ref('{{ qrcodeLink }}')

const changeMessageTemplateText = (slug) => {
  const translationBase = 'pages.contests.contestsCreatePage.preview'
  contest.value.messageTemplate.content = t(`${translationBase}.messageTemplate.content.${slug}`, {
    rewardTag: placeholderReward.value
  })
  contest.value.messageTemplateQrcode.content = t(`${translationBase}.messageTemplateQrcode.content.${slug}`, {
    rewardTag: placeholderReward.value,
    qrcodeLink: placeholderQrcodeLink.value
  })
  contest.value.messageTemplate.subject = t(`${translationBase}.messageTemplate.subject`)
}

const updateChannel = () => {
  const selectedChannelId = channelTypeModel.value.id
  const selectedChannelIdSlug = channelTypeModel.value.slug

  contest.value.messageTemplateQrcode.messageChannelId = selectedChannelId
  contest.value.messageTemplate.messageChannelId = selectedChannelId
  contest.value.sendingNotificationText = t(
    `pages.contests.contestsCreatePage.preview.messageTemplate.sendingNotificationText.${selectedChannelIdSlug}`
  )

  changeMessageTemplateText(selectedChannelIdSlug)
}

const currentBoostType = ref<Boost>({ id: '', description: '', title: '' })
onMounted(() => {
  if (editMode.value || props.isTemplateMode) {
    currentBoostType.value.id = contest.value?.contestGameBoostType?.slug
  } else {
    currentBoostType.value = boosts.find((boost) => boost.id === objectiveSlug.value)
  }
  const currentBoost = boosts.filter((boost) => boost.id === currentBoostType.value?.id)

  selectedBoost.value = currentBoost[0] ?? boosts[0]
  if (!props.isEditMode && isEmptyDuplicateContestItem.value) {
    updateChannel()

    emitSelectedBoost(currentBoost?.length > 0 ? currentBoost[0] : boosts[0])
  }
})

const storesPermission = computed(() => getStoresByPermission('contest-game.create'))
const isStoreBlockVisible = computed(() => storesPermission.value.length)

const hasRightObjective = computed(() => objectiveSlug.value !== 'google')

watch(
  () => props.getContest.contestGameBoostType,
  () => {
    selectedBoost.value = boosts.find((boost) => boost.id === props.getContest.contestGameBoostType?.slug)
    emitSelectedBoost(selectedBoost.value)
    let index
    switch (contest.value.daysBetweenParticipations) {
      case 0:
        index = 0
        break
      case 1:
        index = 1
        break
      default:
        index = 2
    }

    selectedPlayableFrequency.value = playableOnceADayItems[index]
  }
)
watch(
  () => props.getContest,
  () => (contest.value = props.getContest)
)

const isBlockVisible = computed(() => {
  if (hasRightObjective.value) {
    return true
  } else {
    return !hasRightObjective.value && !isUserFirstContest.value
  }
})

const isUrlModalOpen = ref(false)

const storesWithoutUrlAction = computed(() => {
  return storesPermission.value.filter(
    (store) => !isStoreAvailable(store.id) && contest.value.stores.includes(store.id)
  )
})

const handleUrlModalClose = async () => {
  isUrlModalOpen.value = false
}

const tipTranslationKey = computed(() => {
  const boostId = selectedBoost.value?.id
  if (boostId && ['google', 'facebook', 'instagram'].includes(boostId)) {
    return `global.storeUrlsModal.${boostId}.tip`
  }
  return 'global.storeUrlsModal.google.tip' // fallback to google
})
</script>
<template>
  <div class="flex flex-col gap-5 xl:flex-row">
    <div class="rounded-md border bg-white px-8 py-4 xl:flex-1">
      <div>
        <div class="border-b">
          <div class="mb-6 flex flex-col">
            <div class="font-medium text-foreground sm:mb-5 sm:mt-px sm:pt-2">
              <div class="flex">
                {{ t('pages.contests.contestsCreatePage.global.form.fields.name') }}
              </div>
              <dt class="mt-1 pr-12 text-xs font-normal text-muted-foreground">
                {{ t('pages.contests.contestsCreatePage.global.form.fields.info') }}
              </dt>
            </div>
            <div class="flex rounded-md">
              <AppFormInput
                id="name"
                v-model="contest.name"
                type="text"
                name="name"
                class="block w-full min-w-0 flex-1 rounded-md border-input focus:border-primary focus:ring-primary sm:text-sm"
                @keyup="contest.slug = contest.name"
              />
            </div>
          </div>
        </div>
        <div class="border-b text-foreground-disabled">
          <div class="my-6 flex flex-col">
            <div class="font-medium text-foreground sm:mb-6">
              <div class="flex">{{ t('pages.contests.contestsCreatePage.global.form.fields.boost.label') }}</div>
              <dt class="mt-1 pr-12 text-xs font-normal text-muted-foreground">
                {{ t('pages.contests.contestsCreatePage.global.form.fields.boost.info') }}
              </dt>
            </div>

            <div class="text-muted-foreground sm:col-span-2">
              <AppRadioCard
                v-model="selectedBoost"
                class="grid grid-cols-2 gap-3"
                :options="boosts"
                @update:model-value="emitSelectedBoost(selectedBoost)"
              />
              <div v-if="selectedBoost !== null && selectedBoost?.id === 'custom'">
                <div>
                  <div class="mt-6 flex flex-col">
                    <label for="targetUrl" class="block text-sm font-medium text-foreground">
                      {{ t('pages.contests.contestsCreatePage.global.form.fields.boost.custom.title') }}
                    </label>
                    <div class="mt-1 sm:col-span-2">
                      <div class="lg:grid lg:grid-cols-2">
                        <div class="flex max-w-lg rounded-md text-foreground lg:col-span-1">
                          <AppFormInput
                            id="targetUrl"
                            v-model="contest.targetUrl"
                            type="text"
                            placeholder="https://www.example.com/"
                            name="targetUrl"
                            class="block w-full min-w-0 flex-1 rounded-md border-input focus:border-primary focus:ring-primary sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isStoreBlockVisible" class="border-b text-foreground-disabled">
          <div class="my-6 flex flex-col">
            <label for="stores" class="block font-medium text-foreground sm:mb-6">
              {{ t('pages.contests.contestsCreatePage.global.form.fields.stores') }}
            </label>

            <div class="mt-1 sm:col-span-2 sm:mt-0">
              <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <div v-for="store in storesPermission" :key="store.id" class="flex items-start gap-4">
                  <AppCheckboxField
                    :id="String(store.id)"
                    v-model="contest.stores"
                    :value="store.id"
                    :label="store.name"
                  />

                  <Socials
                    :hasFacebook="hasFacebook(store)"
                    :hasInstagram="hasInstagram(store)"
                    :hasGoogle="hasGoogle(store)"
                  />
                </div>
              </div>
              <div class="mt-6 text-center text-xs">
                <button type="button" class="text-primary hover:text-primary/80 focus:outline-hidden" @click="checkAll">
                  {{ t('global.checkboxes.checkAll') }}
                </button>
                /
                <button
                  type="button"
                  class="text-primary hover:text-primary/80 focus:outline-hidden"
                  @click="uncheckAll"
                >
                  {{ t('global.checkboxes.uncheckAll') }}
                </button>
              </div>
            </div>

            <BaseTip
              v-if="storesWithoutUrlAction.length"
              severity="error"
              class="mt-5"
              :text="
                t(tipTranslationKey, {
                  count: storesWithoutUrlAction.length
                })
              "
            >
              <template #button>
                <button
                  class="w-max flex items-center text-sm font-medium text-red-600 cursor-pointer"
                  @click="isUrlModalOpen = true"
                >
                  {{ t('global.storeUrlsModal.update') }}
                  <BaseIcon name="HeroArrowLongRightIconSolid" class="ml-3 h-5 w-5 shrink-0 text-red-600" />
                </button>
              </template>
            </BaseTip>

            <StoreUrlsModal
              :type="(selectedBoost?.id as 'google' | 'facebook' | 'instagram' | 'none' | 'custom' | '') || ''"
              :is-open="isUrlModalOpen"
              :stores="storesWithoutUrlAction"
              @close="handleUrlModalClose"
              @updated="handleUrlModalClose"
            />
          </div>
        </div>

        <div v-if="isBlockVisible" class="border-b text-foreground-disabled">
          <div class="my-6 flex flex-col">
            <label for="dateRangeValue" class="block font-medium text-foreground sm:mb-6">
              {{ t('pages.contests.contestsCreatePage.global.form.fields.period.field') }}
              <p class="mt-1 text-xs font-normal text-muted-foreground">
                {{ t('pages.contests.contestsCreatePage.global.form.fields.period.help') }}
              </p>
            </label>
            <div class="mt-1 sm:col-span-2 sm:mt-0">
              <div class="gap-3 lg:grid lg:grid-cols-2">
                <div class="rounded-md lg:col-span-1">
                  <LitepieDatepicker
                    v-model="dateRangeValue"
                    as-single
                    use-range
                    :disable-date="disableDate"
                    i18n="fr"
                    :formatter="{
                      date: 'DD/MM/YYYY',
                      month: 'MMM'
                    }"
                    @update:model-value="dateRangeChanged"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="border-b text-foreground-disabled">
          <div class="my-6 flex flex-col">
            <div class="font-medium text-foreground sm:mb-6">
              <label for="isPlayableOnceADay" class="block">
                {{ t('pages.contests.contestsCreatePage.global.form.fields.isPlayableOnceADay.label') }}
                <p class="mt-1 text-xs font-normal text-muted-foreground">
                  {{ t('pages.contests.contestsCreatePage.global.form.fields.isPlayableOnceADay.help') }}
                </p>
              </label>
            </div>
            <div class="sm:col-span-2">
              <div>
                <fieldset>
                  <AppRadioCard
                    v-model="selectedPlayableFrequency"
                    class="grid grid-cols-2 gap-3"
                    :options="playableOnceADayItems"
                  />
                </fieldset>
              </div>
            </div>
          </div>
          <div v-if="selectedPlayableFrequency?.id === 'customize'" class="my-6 flex flex-col">
            <div class="font-medium text-foreground">
              <label for="numberOfDays" class="block">
                {{ t('pages.contests.contestsCreatePage.global.form.fields.isPlayableOnceADay.days') }}
              </label>
            </div>
            <div class="mt-4 grid grid-cols-1 gap-3 sm:col-span-2 lg:grid-cols-2">
              <AppFormInput
                id="daysNumber"
                v-model="contest.daysBetweenParticipations"
                type="number"
                class="block w-full min-w-0 flex-1 rounded-md text-foreground sm:text-sm"
                :disabled="false"
              />
            </div>
          </div>
        </div>
        <div class="border-b text-foreground-disabled">
          <div class="my-6 flex flex-col">
            <div class="font-medium text-foreground sm:mb-6">
              <label for="isOtpRequired" class="block">
                {{ t('pages.contests.contestsCreatePage.global.form.fields.emailVerification.label') }}
                <p class="mt-1 text-xs font-normal text-muted-foreground">
                  {{ t('pages.contests.contestsCreatePage.global.form.fields.emailVerification.help') }}
                </p>
              </label>
            </div>
            <div class="sm:col-span-2">
              <BaseSwitch
                id="isOtpRequired"
                v-model="contest.isOtpRequired"
                :label="
                  contest.isOtpRequired
                    ? t('pages.contests.contestsCreatePage.global.form.fields.emailVerification.switchActivated')
                    : t('pages.contests.contestsCreatePage.global.form.fields.emailVerification.switchDesactivated')
                "
                font-color-gradient="600"
                :font-color="contest.isOtpRequired ? 'green' : 'red'"
                is-reversed
              />
            </div>
          </div>
        </div>
        <div class="border-b border-border">
          <div class="my-6 flex flex-col">
            <div class="font-medium text-foreground sm:mb-6">
              <label class="block">
                {{ t('pages.contests.contestsCreatePage.message.title') }}
              </label>
              <p class="mt-1 text-xs font-normal text-muted-foreground">
                {{ t('pages.contests.contestsCreatePage.message.firstDescription') }}
              </p>
            </div>
            <div class="sm:col-span-2">
              <fieldset>
                <AppRadioCard
                  v-model="channelTypeModel"
                  :options="channelType"
                  class="grid w-full grid-cols-2 gap-3"
                  @update:model-value="updateChannel"
                />
                <div v-if="channelTypeModel?.id === 2">
                  <div class="my-6 font-medium text-foreground">
                    <label class="block">
                      {{ t('pages.contests.contestsCreatePage.message.form.fields.emailObjectCustom') }}
                    </label>
                    <p class="mt-1 text-xs font-normal text-muted-foreground">
                      {{ t('pages.contests.contestsCreatePage.message.form.fields.emailObjectCustomDescription') }}
                    </p>
                  </div>
                  <AppFormInput
                    id="customEmailTitle"
                    v-model="contest.messageTemplate.subject"
                    type="text"
                    class="block w-full min-w-0 flex-1 rounded-md border-input focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div v-if="isBlockVisible" class="my-6 flex flex-col border-b">
          <div class="font-medium text-foreground sm:mb-5">
            <label for="conditionUrl" class="block">
              {{ t('pages.contests.contestsCreatePage.global.form.fields.conditions.label') }}
            </label>
            <p class="mt-1 text-xs font-normal text-muted-foreground">
              {{ t('pages.contests.contestsCreatePage.global.form.fields.conditions.info') }}
            </p>
          </div>
          <div class="mb-6 mt-1 sm:col-span-2 sm:mt-0">
            <div>
              <fieldset class="mt-1 sm:mt-0">
                <AppFormInput
                  id="conditionUrl"
                  v-model="contest.conditionsUrl"
                  type="text"
                  name="conditionUrl"
                  :placeholder="t('pages.contests.contestsCreatePage.global.form.fields.conditions.placeholder')"
                />
              </fieldset>
            </div>
          </div>
        </div>
        <div
          class="mt-6 flex flex-col justify-between gap-5 rounded-md bg-muted p-4 pb-0 md:flex-row md:gap-11 md:p-6 md:pb-0"
        >
          <div class="md:pb-6">
            <h4 class="mb-3 font-medium text-foreground">
              {{ t('pages.contests.contestsCreatePage.global.form.fields.preview.title') }}
            </h4>
            <div class="flex max-w-md flex-wrap items-center">
              <p class="mb-8 text-xs font-medium text-foreground-secondary">
                <I18nT keypath="pages.contests.contestsCreatePage.global.form.fields.preview.description">
                  <template #link>
                    <a
                      href="https://heypongohelp.zendesk.com/hc/fr/articles/27321836859793-Quels-sont-les-avantages-%C3%A0-communiquer-le-lien-de-ma-web-app"
                      target="_blank"
                      class="font-bold text-primary underline inline-flex items-center"
                    >
                      <span>{{ t('pages.contests.contestsCreatePage.global.form.fields.preview.link') }}</span>
                      <BaseIcon
                        name="HeroArrowTopRightOnSquareIconSolid"
                        class="ml-1 inline-block h-5 w-5 font-bold text-primary"
                      />
                    </a>
                  </template>
                </I18nT>
              </p>
            </div>
            <BaseSwitch
              id="uniqueQrCode"
              v-model="contest.isVisibleInWebapp"
              :label="
                contest.isVisibleInWebapp
                  ? t('pages.campaign.campaignCreatePage.block.qrCodeBlock.switchActivated')
                  : t('pages.campaign.campaignCreatePage.block.qrCodeBlock.switchDesactivated')
              "
              font-color-gradient="600"
              :font-color="contest.isVisibleInWebapp ? 'green' : 'red'"
              is-reversed
            />
          </div>
          <img :src="webappPreview" alt="webappPreview" class="w-full object-contain md:w-1/3" />
        </div>
      </div>
    </div>

    <ContestsPreview
      :key="contest.logo"
      :contest="contest"
      :mechanic="contest.mechanic"
      :selected-boost="selectedBoost"
    />
  </div>
</template>
